import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI } from '../services/api';
import { useSessionTimeout } from '../hooks/useSessionTimeout';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'manager';
  emp_id: string;
  profilePicture?: string;
  profilePictureFile?: {
    fileId: string;
    driveId: string;
    fileName: string;
    webUrl: string;
  };
  managerEmail?: string;
  managerName?: string;
  designation?: string;
  department?: string;
  office_location?: string;
  phone?: string;
  mobile_phone?: string;
  hireDate?: string;
  dob?: string;
  job_title?: string;
  address?: string;
  status?: string;
  isVerified?: boolean;
  createdBy?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithMicrosoft: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  isAuthenticated: boolean;
  setAuthFromStorage: () => void;
  showSessionWarning: boolean;
  remainingSessionTime: number;
  extendSession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [remainingSessionTime, setRemainingSessionTime] = useState(0);
  const sessionTimeoutRef = React.useRef<{ reset: () => void } | null>(null);

  // Initialize session auth state from localStorage
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser && storedUser !== 'undefined') {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && typeof parsedUser === 'object') {
            setToken(storedToken);
            setUser(parsedUser);
          } else {
            // Invalid user object, clear storage
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
          }
        } catch (parseError) {
          console.error('Failed to parse stored user:', parseError);
          // Clear invalid data
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const setAuthFromStorage = useCallback(() => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser && storedUser !== 'undefined') {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && typeof parsedUser === 'object') {
            setToken(storedToken);
            setUser(parsedUser);
          } else {
            // Invalid user object, clear storage
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        } catch (parseError) {
          console.error('Failed to parse stored user:', parseError);
          // Clear invalid data
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Error setting auth from storage:', error);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await authAPI.login(email, password);
      const { token: newToken, user: userData } = response.data;

      if (newToken && userData) {
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setToken(newToken);
        setUser(userData);
      } else {
        throw new Error('Invalid response from server: missing token or user data');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error.response?.data || { message: 'Login failed' };
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithMicrosoft = useCallback(async (code: string) => {
    try {
      setLoading(true);
      const response = await authAPI.microsoftCallback(code);
      const { token: newToken, user: userData } = response.data;

      if (newToken && userData) {
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setToken(newToken);
        setUser(userData);
      } else {
        throw new Error('Invalid response from server: missing token or user data');
      }
    } catch (error: any) {
      console.error('Microsoft login error:', error);
      throw error.response?.data || { message: 'Microsoft login failed' };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear app authentication
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Clear MSAL cache to prevent token reuse on next login
      // This removes all cached tokens, accounts, and auth state
      const msalKeys = Object.keys(localStorage).filter(
        (key) => key.includes('msal') || key.includes('microsoft')
      );
      msalKeys.forEach((key) => localStorage.removeItem(key));

      // Also clear session storage
      sessionStorage.clear();

      setToken(null);
      setUser(null);
      setShowSessionWarning(false);
      setLoading(false);
      window.location.replace('/login');
    }
  }, []);

  const handleSessionTimeout = useCallback(() => {
    setShowSessionWarning(false);
    logout();
  }, [logout]);

  const handleSessionWarning = useCallback((remainingTime: number) => {
    setShowSessionWarning(true);
    setRemainingSessionTime(remainingTime);
  }, []);

  const extendSession = useCallback(() => {
    setShowSessionWarning(false);
    setRemainingSessionTime(0);
    if (sessionTimeoutRef.current) {
      sessionTimeoutRef.current.reset();
    }
  }, []);

  sessionTimeoutRef.current = useSessionTimeout({
    onTimeout: handleSessionTimeout,
    onWarning: handleSessionWarning,
    inactivityTimeout: 15 * 60 * 1000,
    sessionTimeout: 24 * 60 * 60 * 1000,
  });

  const getCurrentUser = useCallback(async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Get user error:', error);
      logout();
    }
  }, [logout]);

  const value: AuthContextType = {
    user,
    token,
    loading,
    login,
    loginWithMicrosoft,
    logout,
    getCurrentUser,
    isAuthenticated: !!token && !!user,
    setAuthFromStorage,
    showSessionWarning,
    remainingSessionTime,
    extendSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
